import React, { useMemo } from 'react';
import { RevenueTrendPanel } from '../../components/dashboard/RevenueTrendPanel';
import { AovTrendPanel } from '../../components/dashboard/AovTrendPanel';
import { DashboardInsightChip } from '../../components/dashboard/DashboardInsightChip';
import { PageSummaryStats } from '../../components/dashboard/PageSummaryStats';
import ErrorBoundary from '../../components/ErrorBoundary';
import { SkeletonChart } from '../../components/ui/SkeletonBlock';
import { buildTrendsInsight } from '../../lib/dashboardInsights';
import { formatMoney, formatNumber } from '../../lib/formatMoney';

function buildTrendsStats(data, aovData, currencyCode, currencyUnavailable) {
  const revenuePoints = data?.revenueByDate || [];
  const totalRevenue = revenuePoints.reduce(
    (sum, point) => sum + Number(point.totalRevenue || point.value || 0),
    0
  );
  const lastAov = aovData?.length
    ? Number(aovData[aovData.length - 1]?.aov || aovData[aovData.length - 1]?.value || 0)
    : Number(data?.summary?.averageOrderValue || 0);
  const invoiceCount = Number(data?.summary?.invoiceCount || 0);
  const money = (amount) =>
    currencyUnavailable ? 'Rates unavailable' : formatMoney(amount, { currency: currencyCode });

  if (!revenuePoints.length && !invoiceCount) return [];

  return [
    {
      title: 'Period Revenue',
      value: money(totalRevenue || data?.summary?.totalRevenue || 0),
      change: '—',
      trend: 'neutral',
      context: 'Sum across trend window',
      featured: true,
    },
    {
      title: 'Latest AOV',
      value: money(lastAov),
      change: '—',
      trend: 'neutral',
      context: 'Average order value',
    },
    {
      title: 'Invoices',
      value: formatNumber(invoiceCount),
      change: '—',
      trend: 'neutral',
      context: 'In selected period',
    },
    {
      title: 'Trend Points',
      value: formatNumber(revenuePoints.length),
      change: '—',
      trend: 'neutral',
      context: 'Days / buckets plotted',
    },
  ];
}

export function TrendsPage({
  committedFilters,
  data,
  panelLoading,
  error,
  currencyCode,
  aovData,
  isFirstLoad,
  currencyUnavailable = false,
  filterSlot = null,
}) {
  const queryKey = committedFilters.queryString || '';
  const stats = useMemo(
    () => buildTrendsStats(data, aovData, currencyCode, currencyUnavailable),
    [data, aovData, currencyCode, currencyUnavailable]
  );
  const insight = useMemo(
    () =>
      buildTrendsInsight({
        revenueByDate: data?.revenueByDate || [],
        aovByDate: aovData,
        currencyCode,
      }),
    [data?.revenueByDate, aovData, currencyCode]
  );

  return (
    <section className="flex w-full min-w-0 flex-col gap-5">
      <PageSummaryStats
        stats={stats}
        isFirstLoad={isFirstLoad}
        error={error}
        emptyMessage="No trend data found for this period. Try a different date range."
      />

      {filterSlot}

      {!isFirstLoad && !error && stats.length > 0 ? (
        <DashboardInsightChip tone={insight.tone} title={insight.title} detail={insight.detail} />
      ) : null}

      {isFirstLoad ? (
        <>
          <SkeletonChart height="h-[360px]" />
          <SkeletonChart height="h-[280px]" />
        </>
      ) : (
        <>
          <div className="min-h-[360px]">
            <ErrorBoundary key={`rev-${queryKey}`}>
              <RevenueTrendPanel
                revenueByDate={data?.revenueByDate || []}
                loading={panelLoading}
                error={error}
                variant="detailed"
                currencyCode={currencyCode}
              />
            </ErrorBoundary>
          </div>
          <ErrorBoundary key={`aov-${queryKey}`}>
            <AovTrendPanel
              aovByDate={aovData}
              currencyCode={currencyCode}
              loading={panelLoading}
              error={error}
              variant="detailed"
            />
          </ErrorBoundary>
        </>
      )}
    </section>
  );
}
